import React from 'react';
import {
	Box,
	FormControlLabel,
	TextField,
	Typography,
	MenuItem,
	RadioGroup,
	Radio,
	Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import InfoButton from 'common/components/InfoButton';
import { CookieNoticeOptions } from 'common/modules/atoms/cookies/constants';
import { Trans, useTranslation } from 'services/localization';
import { CookieNoticeOption, CookieSettings } from 'common/modules/atoms/cookies/types';
import Banner from 'components/Banner';

interface Props {
	value: CookieSettings;
	onChange: (value: CookieSettings) => void;
}

export const CookiePolicy = ({ value, onChange }: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	return (
		<>
			<Box display="flex" alignItems="center">
				<Typography variant="subtitle1" className={classes.subtitle}>
					{t('privacy.onlineCookiePolicy', 'Online store cookie policy')}
				</Typography>
				<Box ml={2}>
					<InfoButton
						title={t('privacy.infoModal.header', 'Cookie usage')}
						tabIndex={-1}
						iconsButtonSize={3}
						content={
							<Box>
								<Typography variant="body2" gutterBottom>
									{t(
										'privacy.infoModal.body1',
										'Rentle online store uses cookies to offer the best consumer experience. Cookies are also necessary to provide certain functionalities for the store, such as the Google Analytics integration.',
									)}
								</Typography>
								<Box mt={1} />
								<Typography variant="body2" gutterBottom>
									{t('privacy.cookieNoticeInfo')}
								</Typography>
								<Box mt={1} />
								<Typography variant="body2" gutterBottom>
									{t(
										'privacy.infoModal.body2',
										'You can choose to enable cookies without an explicit consent, display a cookie consent banner or disable cookies completely. Note that disabling cookies completely might negatively affect the end-user experience and limit some fuctionalities we are able to offer.',
									)}
								</Typography>
							</Box>
						}
						buttonText={t('common:greetings.okBtn')}
					/>
				</Box>
			</Box>
			<Typography variant="caption" component="div">
				{t(
					'privacy.cookieNoticeInfo',
					'Under the General Data Protection Regulation (GDPR) or other private regulations, visitors to your website must consent to being tracked. This means that you must display a notice telling visitors that you use cookie tracking, and offer them the choice to opt in to being tracked.',
				)}
			</Typography>
			<Box mb={2} />
			<Box>
				<RadioGroup
					aria-label="cookieNotice"
					name="showCookieNotice"
					value={value.default}
					onChange={(e) =>
						onChange({
							...value,
							default: e.target.value as CookieNoticeOption,
							embed: e.target.value as CookieNoticeOption,
						})
					}
				>
					<FormControlLabel
						control={<Radio color="primary" value={CookieNoticeOptions.ENABLE} />}
						label={t('privacy.enableWithoutNotice', 'Enable cookies without notice')}
					/>
					<FormControlLabel
						control={<Radio color="primary" value={CookieNoticeOptions.SHOW_NOTICE} />}
						label={t('privacy.showNotice', 'Show cookie notice')}
					/>
					{value.default === CookieNoticeOptions.SHOW_NOTICE && (
						<Box ml="31px" mt={1.5} mb={2} maxWidth={300}>
							<TextField
								value={value.embed}
								onChange={(e) =>
									onChange({
										...value,
										embed: e.target.value as CookieNoticeOption,
									})
								}
								label={t('privacy.embeddedStoreCookies', 'Edit configuration for embedded store')}
								select
								variant="outlined"
								fullWidth
							>
								<MenuItem value={CookieNoticeOptions.ENABLE}>
									{t('privacy.enableWithoutNotice', 'Enable cookies without notice')}
								</MenuItem>
								<MenuItem value={CookieNoticeOptions.SHOW_NOTICE}>
									{t('privacy.showNotice', 'Show cookie notice')}
								</MenuItem>
								<MenuItem value={CookieNoticeOptions.DISABLE}>
									{t('privacy.disableAllCookies', 'Disable all cookies')}
								</MenuItem>
							</TextField>
						</Box>
					)}

					<FormControlLabel
						control={<Radio color="primary" value={CookieNoticeOptions.DISABLE} />}
						label={
							<>
								<Typography variant="body1">
									{t('privacy.disableAllCookies', 'Disable all cookies')}
								</Typography>
							</>
						}
					/>
					<Box ml={4} mt={-1}>
						<Typography variant="caption" color="secondary">
							{t(
								'privacy.disableAllCookiesInfo',
								'This can negatively affect the end-user experiece. You will not receive statistics of the usage of your site.',
							)}
						</Typography>
					</Box>
				</RadioGroup>
			</Box>
			<Box mt={5}>
				<Banner
					description={
						<span>
							<Trans
								i18nKey="privacy.passConsentViaParameter"
								defaults="You can also pass cookie consent from your website via <paramBtn>query parameters</paramBtn>"
								components={{
									paramBtn: (
										<a
											target="_blank"
											rel="noreferrer"
											href="https://support.rentle.io/en/how-to-configure-cookie-consent-in-rentle#consent-via-query-parameters"
											className={classes.link}
										>
											{'query parameters'}
										</a>
									),
								}}
							/>
						</span>
					}
					variant="info"
				/>
			</Box>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		subtitle: {
			fontSize: '1.8rem',
		},
		link: {
			color: theme.palette.primary.main,
			textDecoration: 'underline',
		},
	}),
);

export default CookiePolicy;
